// Today's Element — deterministic per calendar day, mapped to current TCM season.
// Each element has a daily-rotating tea / point / mantra (7-item rotations).

export const SEASONS = [
  { element: "Wood",  start: { m: 2,  d: 4  }, label: "Spring",       color: "#3A5A40", organ: "Liver & Gallbladder" },
  { element: "Fire",  start: { m: 5,  d: 5  }, label: "Summer",       color: "#B8866D", organ: "Heart & Small Intestine" },
  { element: "Earth", start: { m: 8,  d: 7  }, label: "Late Summer",  color: "#A67C52", organ: "Spleen & Stomach" },
  { element: "Metal", start: { m: 9,  d: 7  }, label: "Autumn",       color: "#6B7A66", organ: "Lung & Large Intestine" },
  { element: "Water", start: { m: 11, d: 7  }, label: "Winter",       color: "#2F4858", organ: "Kidney & Bladder" },
];
// Months are 1-indexed for readability.

const ROTATIONS = {
  Wood: {
    tea:    ["Chrysanthemum & goji", "Spearmint", "Lemon balm", "Green tea", "Hawthorn berry", "Schisandra", "Dandelion root"],
    point:  [
      { n: "LV 3 (Tai Chong)", how: "Top of the foot, between big and second toe. Hold 1 minute, each side." },
      { n: "GB 20 (Feng Chi)", how: "Base of skull, two thumb-widths from spine. Circles for 30 seconds." },
      { n: "LV 14 (Qi Men)",   how: "Just below the breast on the rib line. Soft press 30 seconds, each side." },
      { n: "GB 34 (Yang Ling Quan)", how: "Outer calf, dip below the knee. Press 1 minute, each side." },
      { n: "LI 4 (He Gu)",     how: "Web between thumb and index. Steady pressure 30 seconds each hand." },
      { n: "LV 2 (Xing Jian)", how: "Between the big and second toe. Press for headache relief." },
      { n: "GV 20 (Bai Hui)",  how: "Top of head. Tap softly 30 times to clear." },
    ],
    mantra: [
      "I bend so I do not break.",
      "There is room for me to grow.",
      "My anger has good information. I listen, then let go.",
      "I move with the season, not against it.",
      "My boundaries are kindness, not walls.",
      "Vision begins with one quiet step.",
      "I plant what I want to harvest.",
    ],
  },
  Fire: {
    tea:    ["Rose & jujube", "Chamomile", "Lotus leaf", "Hibiscus", "Tulsi", "Linden flower", "Lavender"],
    point:  [
      { n: "HT 7 (Shen Men)", how: "Inner wrist crease, pinky side. Hold softly 1 minute." },
      { n: "PC 6 (Nei Guan)", how: "Three finger-widths below the inner wrist. Press for calm." },
      { n: "PC 8 (Lao Gong)", how: "Centre of palm where middle finger touches when fist closes. Massage 30 seconds." },
      { n: "HT 3 (Shao Hai)", how: "Inner elbow crease. Press for emotional regulation." },
      { n: "SI 3 (Hou Xi)",   how: "Side of the hand below the pinky. Press for shoulder tension." },
      { n: "GV 24 (Shen Ting)", how: "Centre of forehead, hairline. Soft circles 1 minute." },
      { n: "Yintang",          how: "Between the eyebrows. Soft press to calm the mind." },
    ],
    mantra: [
      "My heart is safe. I can be warm without burning.",
      "Joy is allowed without performance.",
      "I receive the love I freely give.",
      "My presence is a gift I do not have to prove.",
      "I trust my heart's quiet knowing.",
      "Connection over perfection.",
      "I am loved exactly as I am today.",
    ],
  },
  Earth: {
    tea:    ["Ginger & jujube", "Roasted barley", "Tangerine peel", "Cinnamon", "Cardamom", "Fennel seed", "Astragalus"],
    point:  [
      { n: "ST 36 (Zu San Li)",  how: "Four fingers below knee, one finger lateral to shin. Press 1 minute." },
      { n: "SP 6 (San Yin Jiao)", how: "Four fingers above the inner ankle. Press for grounding." },
      { n: "CV 12 (Zhong Wan)",   how: "Halfway between belly button and breastbone. Soft hand for 1 minute." },
      { n: "SP 4 (Gong Sun)",     how: "Inside of the foot, below the big toe joint. Press 30 seconds." },
      { n: "ST 25 (Tian Shu)",    how: "Two finger-widths beside the navel. Press for digestion." },
      { n: "CV 6 (Qi Hai)",       how: "Two fingers below the navel. Place hand and breathe slowly." },
      { n: "SP 9 (Yin Ling Quan)", how: "Inside of the leg, below the knee. Press for water retention." },
    ],
    mantra: [
      "I am held by the same earth I hold others on.",
      "I take care of myself like I take care of those I love.",
      "Enough is a place I am allowed to live.",
      "I nourish, I am nourished.",
      "I am the centre, not the edge.",
      "I let go of what is not mine to carry.",
      "Today I will eat slowly and with thanks.",
    ],
  },
  Metal: {
    tea:    ["Pear & almond", "Lily bulb", "White peony", "Honeysuckle", "Mulberry leaf", "Chrysanthemum", "Goji"],
    point:  [
      { n: "LU 9 (Tai Yuan)", how: "Inner wrist, thumb side. Gentle press for breath." },
      { n: "LI 4 (He Gu)",    how: "Web between thumb and index. Press for clearing." },
      { n: "LU 1 (Zhong Fu)", how: "Below the collarbone, shoulder side. Press for grief in the chest." },
      { n: "LU 7 (Lie Que)",  how: "Inside of forearm, two fingers above the wrist. Press for cough." },
      { n: "LI 20 (Ying Xiang)", how: "Beside the nostrils. Circle softly to open sinuses." },
      { n: "BL 13 (Fei Shu)", how: "Back, between shoulder blades. Use a tennis ball if alone." },
      { n: "GV 14 (Da Zhui)", how: "Base of neck, between vertebrae. Warm with hand for immunity." },
    ],
    mantra: [
      "I keep what is precious. I release what is finished.",
      "Grief has a shape and I let it have it.",
      "My breath is my home.",
      "Less is enough. Quiet is fullness.",
      "I refine, I do not exhaust.",
      "What leaves makes room for what arrives.",
      "I belong to my own clarity.",
    ],
  },
  Water: {
    tea:    ["Goji & longan", "Roasted black bean", "Schisandra", "Reishi", "Cordyceps", "Black sesame", "He Shou Wu"],
    point:  [
      { n: "KD 1 (Yong Quan)", how: "Sole of the foot, below the ball. Press for grounding." },
      { n: "KD 3 (Tai Xi)",    how: "Inner ankle, midway between bone and Achilles. Tonify reserves." },
      { n: "BL 23 (Shen Shu)", how: "Lower back, beside the spine at kidney level. Warm hand for 1 minute." },
      { n: "CV 4 (Guan Yuan)", how: "Three fingers below the navel. Place hand, breathe deeply." },
      { n: "KD 6 (Zhao Hai)",  how: "Inner ankle, below the bone. Press for sleep." },
      { n: "GV 4 (Ming Men)",  how: "Lower back, between vertebrae L2-L3. Warm with hand." },
      { n: "BL 10 (Tian Zhu)", how: "Base of skull, on the neck muscles. Soft circles for fear." },
    ],
    mantra: [
      "I am held by the deep.",
      "I trust what I cannot see.",
      "Rest is not retreat. It is medicine.",
      "My reserves are sacred. I refill them.",
      "Fear is a small messenger. I listen and continue.",
      "The river knows the way.",
      "Stillness is a kind of strength.",
    ],
  },
};

function pad(n) { return n < 10 ? "0" + n : "" + n; }
function dateKey(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

export function currentSeason(now = new Date()) {
  const year = now.getFullYear();
  // Build sorted list of season-start timestamps for this year
  const starts = SEASONS.map((s) => ({
    ...s,
    ts: new Date(year, s.start.m - 1, s.start.d).getTime(),
  })).sort((a, b) => a.ts - b.ts);

  const t = now.getTime();
  let chosen = starts[starts.length - 1];
  let next = null;
  for (let i = 0; i < starts.length; i++) {
    if (t >= starts[i].ts) {
      chosen = starts[i];
      next = starts[i + 1] || { ...starts[0], ts: new Date(year + 1, starts[0].start.m - 1, starts[0].start.d).getTime() };
    }
  }
  if (!next) {
    // Before the first season start of the year → still in previous year's Water
    chosen = { ...starts[starts.length - 1], ts: new Date(year - 1, starts[starts.length - 1].start.m - 1, starts[starts.length - 1].start.d).getTime() };
    next = starts[0];
  }
  return { season: chosen, next };
}

export function daysUntil(ts, now = new Date()) {
  return Math.max(0, Math.ceil((ts - now.getTime()) / (1000 * 60 * 60 * 24)));
}

export function todayContent(now = new Date()) {
  const { season, next } = currentSeason(now);
  const rot = ROTATIONS[season.element];
  // Day-of-year picks the rotation index
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const day = Math.floor(diff / (1000 * 60 * 60 * 24));
  const i = day % 7;
  return {
    dateKey: dateKey(now),
    season,
    nextSeason: next,
    daysUntilNextSeason: daysUntil(next.ts, now),
    tea: rot.tea[i],
    point: rot.point[i],
    mantra: rot.mantra[i],
  };
}
